/**
 * ActivityList component - Renders an ordered list of activity cards with drag-and-drop
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import type { Activity } from '../../types/activity';
import type { Day } from '../../types/trip';
import SortableActivityCard from './SortableActivityCard';
import ActivityCard from './ActivityCard';

interface ActivityListProps {
  activities: Activity[];
  onEditActivity: (activity: Activity) => void;
  onDeleteActivity: (activity: Activity) => void;
  onReorderActivity: (activityId: string, newOrder: number) => void;
  onMoveToDay?: (activityId: string, dayIndex: number) => void;
  onUnscheduleActivity?: (activity: Activity) => void;
  onSelectActivity?: (activity: Activity) => void;
  onTogglePrimary?: (activity: Activity) => void;
  availableDays?: Day[];
  /** 停用拖曳排序（例如清單已被篩選成子集合時，避免子集合順序覆蓋掉當天完整順序） */
  disableDrag?: boolean;
}

export default function ActivityList({ activities, onEditActivity, onDeleteActivity, onReorderActivity, onMoveToDay, onUnscheduleActivity, onSelectActivity, onTogglePrimary, availableDays, disableDrag }: ActivityListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (activities.length === 0) {
    return null;
  }

  if (disableDrag) {
    return (
      <div className="space-y-3">
        {activities.map((activity, index) => (
          <ActivityCard
            key={activity.id}
            activity={activity}
            index={index}
            onEdit={() => onEditActivity(activity)}
            onDelete={() => onDeleteActivity(activity)}
            onMoveToDay={onMoveToDay ? (dayIndex) => onMoveToDay(activity.id, dayIndex) : undefined}
            onUnschedule={onUnscheduleActivity ? () => onUnscheduleActivity(activity) : undefined}
            onClick={onSelectActivity ? () => onSelectActivity(activity) : undefined}
            onTogglePrimary={onTogglePrimary ? () => onTogglePrimary(activity) : undefined}
            availableDays={availableDays}
          />
        ))}
      </div>
    );
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = activities.findIndex((a) => a.id === active.id);
      const newIndex = activities.findIndex((a) => a.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderActivity(active.id as string, newIndex);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeActivity = activeId ? activities.find((a) => a.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={activities.map((a) => a.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <SortableActivityCard
              key={activity.id}
              activity={activity}
              index={index}
              onEdit={() => onEditActivity(activity)}
              onDelete={() => onDeleteActivity(activity)}
              onMoveToDay={onMoveToDay ? (dayIndex) => onMoveToDay(activity.id, dayIndex) : undefined}
              onUnschedule={onUnscheduleActivity ? () => onUnscheduleActivity(activity) : undefined}
              onClick={onSelectActivity ? () => onSelectActivity(activity) : undefined}
              onTogglePrimary={onTogglePrimary ? () => onTogglePrimary(activity) : undefined}
              availableDays={availableDays}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeActivity ? (
          <ActivityCard
            activity={activeActivity}
            index={activities.findIndex((a) => a.id === activeId)}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
